import { z } from 'zod';
import { VALIDATION_LIMITS } from '../utils/constants';
import { BrewingMethod, RecipeDifficulty, GrindSize } from '../types/recipe';
export const recipeIngredientSchema = z.object({
    name: z.string().min(1, 'Ingredient name is required'),
    amount: z.number().positive('Amount must be positive'),
    unit: z.string().min(1, 'Unit is required'),
    notes: z.string().optional(),
});
export const recipeStepSchema = z.object({
    step_number: z.number().positive('Step number must be positive'),
    title: z.string().min(1, 'Step title is required'),
    description: z.string().min(1, 'Step description is required'),
    duration_seconds: z.number().positive().optional(),
    temperature_celsius: z.number().min(0).max(100).optional(),
    notes: z.string().optional(),
    image_url: z.string().url().optional(),
});
export const brewingParametersSchema = z.object({
    grind_size: z.nativeEnum(GrindSize),
    water_temperature_celsius: z.number().min(0).max(100),
    coffee_to_water_ratio: z
        .string()
        .regex(/^\d+(\.\d+)?$/, 'Invalid ratio format'),
    bloom_time_seconds: z.number().positive().optional(),
    total_brew_time_seconds: z.number().positive(),
    pressure_bars: z.number().positive().optional(),
});
export const recipeSchema = z.object({
    title: z
        .string()
        .min(VALIDATION_LIMITS.RECIPE_TITLE.MIN_LENGTH, 'Title is required')
        .max(VALIDATION_LIMITS.RECIPE_TITLE.MAX_LENGTH, 'Title too long'),
    description: z
        .string()
        .min(VALIDATION_LIMITS.RECIPE_DESCRIPTION.MIN_LENGTH, `Description must be at least ${VALIDATION_LIMITS.RECIPE_DESCRIPTION.MIN_LENGTH} characters`)
        .max(VALIDATION_LIMITS.RECIPE_DESCRIPTION.MAX_LENGTH, `Description must be less than ${VALIDATION_LIMITS.RECIPE_DESCRIPTION.MAX_LENGTH} characters`),
    brewing_method: z.nativeEnum(BrewingMethod),
    difficulty: z.nativeEnum(RecipeDifficulty),
    servings: z.number().min(1).max(20),
    prep_time_minutes: z.number().min(1).max(180),
    total_time_minutes: z.number().min(1).max(480),
    brewing_parameters: brewingParametersSchema,
    equipment_ids: z.array(z.string()).optional().default([]),
    ingredients: z
        .array(recipeIngredientSchema)
        .min(1, 'At least one ingredient is required'),
    steps: z.array(recipeStepSchema).min(1, 'At least one step is required'),
    tags: z.array(z.string()).optional().default([]),
    images: z.array(z.string().url()).optional().default([]),
    video_url: z.string().url().optional(),
    is_public: z.boolean().default(true),
});
export const recipeUpdateSchema = recipeSchema.partial();
export const recipeFiltersSchema = z.object({
    brewing_method: z.array(z.nativeEnum(BrewingMethod)).optional(),
    difficulty: z.array(z.nativeEnum(RecipeDifficulty)).optional(),
    prep_time_max: z.number().positive().optional(),
    total_time_max: z.number().positive().optional(),
    tags: z.array(z.string()).optional(),
    creator_id: z.string().optional(),
    is_featured: z.boolean().optional(),
    min_rating: z.number().min(0).max(5).optional(),
});
export const recipeListParamsSchema = z.object({
    page: z.number().min(1).optional().default(1),
    limit: z.number().min(1).max(100).optional().default(20),
    search: z.string().optional(),
    filters: recipeFiltersSchema.optional(),
    sort_by: z
        .enum([
        'created_at',
        'updated_at',
        'rating',
        'view_count',
        'favorite_count',
    ])
        .optional()
        .default('created_at'),
    sort_order: z.enum(['asc', 'desc']).optional().default('desc'),
});
export const recipeCommentSchema = z.object({
    content: z
        .string()
        .min(VALIDATION_LIMITS.COMMENT.MIN_LENGTH, 'Comment cannot be empty')
        .max(VALIDATION_LIMITS.COMMENT.MAX_LENGTH, 'Comment too long'),
    parent_id: z.string().optional(),
});
export const recipeRatingSchema = z.object({
    rating: z.number().min(1).max(5),
    review: z.string().optional(),
});
